import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, UserCircle, Phone, Lock, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateClient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if called from handler settings
  const isHandler = location.pathname.includes('/handler/');
  // Session-47: land on Handler Settings when handler used it — the whole
  // point of navigating from Settings is to land back on Settings.
  const backRoute = isHandler ? '/handler-settings' : '/admin-settings';
  const apiEndpoint = isHandler
    ? `${API}/handler/clients/create`
    : `${API}/admin/clients/create`;
    
  const handleBackClick = () => {
    // Respect explicit `state.from` when present so nested flows return
    // to their true origin.
    const from = location.state?.from;
    if (from) return navigate(from, { replace: true });
    navigate(backRoute, { replace: true });
  };

  const [formData, setFormData] = useState({
    username: '',
    phone: '',
    password: ''
  });
  const [creating, setCreating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error('Phone must be 10 digits');
      return;
    }

    if (!formData.password.trim()) {
      toast.error('Password is required');
      return;
    }

    if (formData.password.length < 5) {
      toast.error('Password must be at least 5 characters');
      return;
    }

    try {
      setCreating(true);
      
      const response = await axios.post(apiEndpoint, {
        username: formData.username.trim(),
        phone: formData.phone.trim(),
        password: formData.password.trim()
      });

      toast.success('✅ Client created successfully!');
      toast.info(`Phone: ${formData.phone} | Password: ${formData.password}`, {
        duration: 5000
      });

      // Reset form
      setFormData({
        username: '',
        phone: '',
        password: ''
      });

      setCreating(false);
    } catch (error) {
      console.error('Create client failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create client');
      setCreating(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 relative z-0">
      {/* Header */}
      <div className="bg-[#25D366] text-white p-4 flex-shrink-0 relative z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackClick}
            className="p-2 hover:bg-green-600 rounded-full transition-colors"
            type="button"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Create Client Account</h1>
            <p className="text-sm text-green-100">
              {isHandler ? 'Handler can create client' : 'Admin can create client'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 flex items-center justify-center relative z-0">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-lg p-6 relative">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Plus size={40} className="text-green-600" weight="bold" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Create New Client</h2>
              <p className="text-sm text-gray-500 mt-1">
                Enter client details to create account
              </p>
            </div>

            <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <UserCircle size={18} weight="bold" className="inline mr-1" />
                  Client Name *
                </label>
                <input
                  type="text"
                  name="username"
                  autoComplete="off"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter client name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              {/* Phone Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone size={18} weight="bold" className="inline mr-1" />
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10 digit mobile number"
                  maxLength={10}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be 10 digits
                </p>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Lock size={18} weight="bold" className="inline mr-1" />
                  Password *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Minimum 5 characters"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, password: 'Abcd1234' }))}
                    className="px-3 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-xs font-semibold whitespace-nowrap"
                    data-testid="default-password-btn"
                  >
                    Default
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 5 characters required
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={creating}
                className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 mt-6"
              >
                <Plus size={24} weight="bold" />
                {creating ? 'Creating Client...' : 'Create Client Account'}
              </button>
            </form>

            {/* Info Box */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Note:</strong> After creation, the client can login using the phone number and password provided here.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateClient;
